// GameScene.ts
import * as THREE from 'three';
import AssetManager from '../managers/AssetManager';
import { applySkinToObject, getTextureForSkin } from '../utils/skin';

export default class GameScene {
  camera: THREE.Camera;
  scene: THREE.Scene;
  collidables: any[];
  animatedLights: Array<{ spotlight: THREE.SpotLight; lightSource: THREE.Object3D; placeholder: any }>;
  mixers: THREE.AnimationMixer[];
  ui: any;
  tankModel?: THREE.Object3D;
  textures: Record<string, any> = {};

  constructor(camera: THREE.Camera, ui: any) {
    this.camera = camera;
    this.scene = new THREE.Scene();
    this.collidables = [];
    this.animatedLights = [];
    this.mixers = [];
    this.ui = ui;
  }

  initScene() {
    this.initLights();
    this.initBaseModels(this.scene, this.collidables);
    this.initModels();
  }

  activate() {
    const changeTankTexture = (skinName: string) => {
      if (this.tankModel) {
        const tex = getTextureForSkin(skinName);
        if (tex) applySkinToObject(this.tankModel, tex);
      }
    };
    this.ui.init(changeTankTexture);
    (this.scene as any).background = AssetManager.getCubeTexture('alaSkyboxTexture');
  }

  deactivate() {
    this.ui.dispose();
  }

  initLights() {
    const ambientLight = new THREE.AmbientLight(0x404040);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(50, 200, 100);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    const fillLight = new THREE.DirectionalLight(0x9090ff, 0.4);
    fillLight.position.set(-50, 50, -100);
    const rimLight = new THREE.DirectionalLight(0xffa066, 0.3);
    rimLight.position.set(100, 10, -50);
    this.scene.add(ambientLight);
    this.scene.add(directionalLight);
    this.scene.add(fillLight);
    this.scene.add(rimLight);
  }

  initBaseModels(scene: THREE.Scene, collidables: any[]) {
    const boxMaterial = new THREE.MeshStandardMaterial({
      map: AssetManager.getTexture('brickColorMap') as any,
      normalMap: AssetManager.getTexture('brickNormalMap') as any,
      roughnessMap: AssetManager.getTexture('brickRoughnessMap') as any,
      metalness: 0.0,
      roughness: 0.9,
    });
    const boxGeometry = new THREE.BoxGeometry(40, 10, 1);
    const box = new THREE.Mesh(boxGeometry, boxMaterial);
    box.receiveShadow = true;
    box.castShadow = true;
    box.position.z = -25;
    box.position.y = 5;
    scene.add(box);
    collidables.push(box);
  }

  initModels() {
    const model = AssetManager.getModel('NOCompleteScene') as THREE.Object3D;
    const animations = AssetManager.getAnimations('NOCompleteScene') as THREE.AnimationClip[] | undefined;
    if (model) {
      model.traverse((child: any) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          if (child.userData?.name?.includes('collision')) {
            new THREE.Box3().setFromObject(child);
            this.collidables.push(child);
          }
          if (typeof child.name === 'string' && child.name.startsWith('LightPlaceholder')) {
            const spotlightInfo = this.createSpotlightWithVisibleSource(child.position, this.scene);
            this.animatedLights.push({
              spotlight: spotlightInfo.spotlight,
              lightSource: spotlightInfo.lightSource,
              placeholder: child,
            });
          }
        }
      });
      this.scene.add(model);
      if (animations && animations.length > 0) {
        const mixer = new THREE.AnimationMixer(model);
        animations.forEach((clip) => {
          const action = mixer.clipAction(clip);
          action.play();
        });
        this.mixers.push(mixer);
      }
    }
  }

  createSpotlightWithVisibleSource(position: THREE.Vector3, scene: THREE.Scene) {
    const spotlight = new THREE.SpotLight(0xffffff, 10);
    spotlight.position.copy(position);
    spotlight.castShadow = true;
    spotlight.angle = Math.PI / 6;
    spotlight.penumbra = 0.3;
    spotlight.decay = 2;
    spotlight.distance = 500;
    const lightSource = this.createVisibleLightSource(position);
    this.scene.add(spotlight);
    this.scene.add(lightSource);
    return { spotlight, lightSource };
  }

  createVisibleLightSource(position: THREE.Vector3) {
    const sphereGeometry = new THREE.SphereGeometry(0.1, 16, 16);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xffffaa });
    const lightSource = new THREE.Mesh(sphereGeometry, sphereMaterial);
    lightSource.position.copy(position);
    return lightSource;
  }

  updateLights() {
    this.animatedLights.forEach(({ spotlight, lightSource, placeholder }) => {
      spotlight.position.copy(placeholder.position);
      spotlight.target.position.set(
        placeholder.position.x,
        placeholder.position.y - 1,
        placeholder.position.z
      );
      lightSource.position.copy(placeholder.position);
    });
  }

  update(deltaTime: number) {
    this.mixers.forEach((mixer) => mixer.update(deltaTime));
    this.updateLights();
  }

  disposeScene() {
    this.scene.traverse((object: any) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((material: any) => material.dispose());
        } else {
          object.material.dispose();
        }
      }
    });
  }

  getThreeScene(): THREE.Scene {
    return this.scene;
  }
}
